import React, { useEffect, useState } from 'react';
import { shopApi } from '../../utils/shopApi';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { toast } from 'sonner';
import { Truck, Save, Plus, Trash2, MapPin } from 'lucide-react';

export default function DeliverySettingsPage() {
  const [settings, setSettings] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    shopApi.get('/shop/admin/delivery-settings').then(r => setSettings(r.data)).catch(() => toast.error('Failed to load'));
  }, []);

  if (!settings) return <div className="text-muted-foreground">Loading…</div>;

  const updateZone = (idx, field, val) => {
    setSettings(s => ({ ...s, zones: s.zones.map((z, i) => i === idx ? { ...z, [field]: field.includes('km') || field.startsWith('fee_') ? parseFloat(val) || 0 : val } : z) }));
  };
  const addZone = () => setSettings(s => ({ ...s, zones: [...s.zones, { id: `z${s.zones.length+1}`, name: 'New zone', min_km: 0, max_km: 5, fee_now: 1500, fee_scheduled: 2000 }] }));
  const removeZone = (idx) => setSettings(s => ({ ...s, zones: s.zones.filter((_, i) => i !== idx) }));

  const save = async () => {
    setBusy(true);
    try {
      await shopApi.put('/shop/admin/delivery-settings', settings);
      toast.success('Delivery settings saved');
    } catch (err) { toast.error(err?.response?.data?.detail || 'Failed'); }
    finally { setBusy(false); }
  };

  return (
    <div className="space-y-6 max-w-3xl">
      <div>
        <h1 className="text-3xl font-serif font-bold flex items-center gap-2"><Truck className="w-7 h-7 text-primary" />Delivery settings</h1>
        <p className="text-sm text-muted-foreground">Distance-based fees from the store origin. Scheduled deliveries can have a different fee.</p>
      </div>

      {/* Store origin */}
      <section className="p-5 bg-white rounded-xl border border-border space-y-3">
        <h2 className="font-semibold flex items-center gap-2"><MapPin className="w-4 h-4 text-primary" />Store origin</h2>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label>Latitude</Label>
            <Input type="number" step="0.0001" value={settings.store_lat} onChange={(e) => setSettings(s => ({...s, store_lat: parseFloat(e.target.value) || 0}))} data-testid="store-lat" />
          </div>
          <div>
            <Label>Longitude</Label>
            <Input type="number" step="0.0001" value={settings.store_lng} onChange={(e) => setSettings(s => ({...s, store_lng: parseFloat(e.target.value) || 0}))} data-testid="store-lng" />
          </div>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label>Free delivery threshold (RWF)</Label>
            <Input type="number" value={settings.free_threshold} onChange={(e) => setSettings(s => ({...s, free_threshold: parseFloat(e.target.value) || 0}))} data-testid="free-threshold" />
            <p className="text-xs text-muted-foreground mt-1">Same-day orders ≥ this amount get free delivery.</p>
          </div>
          <div>
            <Label>Scheduled lead-time (hours)</Label>
            <Input type="number" value={settings.scheduled_lead_hours || 2} onChange={(e) => setSettings(s => ({...s, scheduled_lead_hours: parseInt(e.target.value) || 0}))} data-testid="lead-hours" />
          </div>
        </div>
      </section>

      {/* Zones */}
      <section className="p-5 bg-white rounded-xl border border-border space-y-3">
        <div className="flex items-center justify-between">
          <h2 className="font-semibold">Distance bands</h2>
          <Button size="sm" variant="outline" onClick={addZone} data-testid="add-zone-btn"><Plus className="w-4 h-4 mr-1" />Add band</Button>
        </div>
        <div className="space-y-2" data-testid="zones-list">
          {settings.zones.map((z, idx) => (
            <div key={idx} className="grid grid-cols-12 gap-2 items-end p-3 rounded-lg bg-muted/20 border border-border" data-testid={`zone-row-${idx}`}>
              <div className="col-span-3"><Label className="text-xs">Name</Label><Input value={z.name} onChange={(e) => updateZone(idx, 'name', e.target.value)} className="h-8" /></div>
              <div className="col-span-2"><Label className="text-xs">Min km</Label><Input type="number" step="0.1" value={z.min_km} onChange={(e) => updateZone(idx, 'min_km', e.target.value)} className="h-8" /></div>
              <div className="col-span-2"><Label className="text-xs">Max km</Label><Input type="number" step="0.1" value={z.max_km} onChange={(e) => updateZone(idx, 'max_km', e.target.value)} className="h-8" /></div>
              <div className="col-span-2"><Label className="text-xs">Fee now (RWF)</Label><Input type="number" value={z.fee_now} onChange={(e) => updateZone(idx, 'fee_now', e.target.value)} className="h-8" data-testid={`fee-now-${idx}`} /></div>
              <div className="col-span-2"><Label className="text-xs">Fee scheduled</Label><Input type="number" value={z.fee_scheduled} onChange={(e) => updateZone(idx, 'fee_scheduled', e.target.value)} className="h-8" data-testid={`fee-sched-${idx}`} /></div>
              <div className="col-span-1 text-right">
                <Button size="icon" variant="ghost" onClick={() => removeZone(idx)} data-testid={`remove-zone-${idx}`}><Trash2 className="w-4 h-4 text-red-500" /></Button>
              </div>
            </div>
          ))}
        </div>
      </section>

      <Button onClick={save} disabled={busy} size="lg" data-testid="save-delivery-settings"><Save className="w-4 h-4 mr-1" />{busy ? 'Saving…' : 'Save changes'}</Button>
    </div>
  );
}
